import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

import {
  Plus,
  Settings,
  Trash2,
  TestTube,
  Zap,
  RefreshCw,
  MoreHorizontal,
  Store,
  CheckCircle,
  XCircle,
  BarChart3,
  PowerOff,
  Power,
  Clock,
  Activity,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';

import {
  useConnectorConfigs,
  useCreateConnectorConfig,
  useUpdateConnectorConfig,
  useDeleteConnectorConfig,
  useBulkSyncProducts,
  useSyncAllProducts,
  useTestConnection,
  useSyncLogs,
  useDebugProduct,
  useAvailablePlatforms,
  useAnalyticsSummary,
  type ExternalPlatformConfig,
  type CreatePlatformConfigRequest,
  type UpdatePlatformConfigRequest,
} from '../../../hooks/useConnectorManagement';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import { PLATFORMS } from '@/features/settings/components/connectors/platforms';
import ConnectorForm from '@/features/settings/components/connectors/ConnectorForm';
import { logError, logInfo } from '@/utils/errorHandler';
import { useAuth } from '@/contexts/AuthContext';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { useQuery } from '@tanstack/react-query';
import { AttributeService } from '@/services/AttributeService';

// Using modular platform definitions from connectors/platforms

// Form schemas
const createPlatformConfigSchema = (i18n: any) =>
  z.object({
    platform: z.string().min(1, i18n.api.validation.platformRequired),
    name: z
      .string()
      .min(1, i18n.api.validation.configurationNameRequired)
      .max(100, i18n.api.validation.nameMaxLength),
    settings: z.record(z.any()).refine(settings => {
      // Validation will be done based on selected platform
      return Object.keys(settings).length > 0;
    }, i18n.api.validation.platformSettingsRequired),
    is_enabled: z.boolean().optional(),
    auto_sync_enabled: z.boolean().optional(),
    sync_interval: z.enum(['disabled', 'hourly', 'daily', 'weekly', 'monthly']).optional(),
  });

type PlatformConfigFormData = z.infer<ReturnType<typeof createPlatformConfigSchema>>;

export function ApiManagement() {
  const i18n = useSettingspageTranslations();

  const { user, checkPermission, loading: authLoading } = useAuth();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState<ExternalPlatformConfig | null>(null);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('');
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Hooks for data management
  const {
    data: configs,
    isLoading: configsLoading,
    refetch: refetchConfigs,
  } = useConnectorConfigs();
  const {
    data: syncLogsData,
    isLoading: syncLogsLoading,
    refetch: refetchLogs,
  } = useSyncLogs(currentPage, pageSize);
  const { data: availablePlatforms = [], isLoading: platformsLoading } = useAvailablePlatforms();
  const createConfigMutation = useCreateConnectorConfig();
  const updateConfigMutation = useUpdateConnectorConfig();
  const deleteConfigMutation = useDeleteConnectorConfig();
  const bulkSyncMutation = useBulkSyncProducts();
  const syncAllMutation = useSyncAllProducts();
  const testConnectionMutation = useTestConnection();
  const debugProductMutation = useDebugProduct();
  const {
    data: analytics,
    isLoading: analyticsLoading,
    refetch: refetchAnalytics,
  } = useAnalyticsSummary();

  // Fetch organization attributes for mapping dropdowns
  const { data: attributes = [], isLoading: isLoadingAttributes } = useQuery({
    queryKey: ['attributes'],
    queryFn: () => AttributeService.fetchAttributes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch price types for mapping dropdowns
  const { priceTypes = [], loading: isLoadingPriceTypes } = usePriceMetadata();

  // Debug logging for attributes and price types
  useEffect(() => {
    if (attributes.length > 0) {
      logInfo('Available attributes loaded', {
        component: 'ApiManagement',
        action: 'loadAttributes',
        metadata: {
          count: attributes.length,
          dataTypes: [...new Set(attributes.map(attr => attr.data_type))],
        },
      });
    }
  }, [attributes]);

  useEffect(() => {
    if (priceTypes.length > 0) {
      logInfo('Available price types loaded', {
        component: 'ApiManagement',
        action: 'loadPriceTypes',
        metadata: { count: priceTypes.length },
      });
    }
  }, [priceTypes]);

  // Debug logging for sync logs
  useEffect(() => {
    if (syncLogsData) {
      logInfo('Sync logs data loaded', {
        component: 'ApiManagement',
        action: 'loadSyncLogs',
        metadata: {
          totalLogs: syncLogsData.results.length,
          successfulLogs: syncLogsData.results.filter(log => log.success).length,
          platforms: [...new Set(syncLogsData.results.map(log => log.platform))],
        },
      });
    }
  }, [syncLogsData]);

  // Form setup
  const form = useForm<PlatformConfigFormData>({
    resolver: zodResolver(createPlatformConfigSchema(i18n)),
    defaultValues: {
      platform: '',
      name: '',
      settings: {},
    },
  });

  // Permission check - same logic as before
  const hasConnectorAccess = (() => {
    // Allow Django superusers (KernLogic staff)
    if (user?.is_superuser) return true;

    // Check organization role-based permissions
    const lowerRole = user?.role?.toString().toLowerCase();

    // Organization owners have full access
    const isOrgOwner = lowerRole === 'owner' || lowerRole === 'orgowner';
    if (isOrgOwner) return true;

    // Organization admins have connector access
    const isOrgAdmin = lowerRole === 'admin';
    if (isOrgAdmin) return true;

    // Check for existing backend permissions
    if (checkPermission && checkPermission('settings.change')) return true;
    if (checkPermission && checkPermission('settings.view')) return true;

    return false;
  })();

  // Show loading state while auth is still initializing
  if (authLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="size-8 animate-spin" />
        <p className="text-muted-foreground">{i18n.api.loadingStates.loadingPermissions}</p>
      </div>
    );
  }

  // Only show access denied if auth is complete and user definitely doesn't have access
  if (!hasConnectorAccess) {
    return (
      <div className="py-12 text-center">
        <Store className="mx-auto mb-4 size-12 text-muted-foreground" />
        <h3 className="mb-2 text-lg font-semibold text-foreground">Admin Access Required</h3>
        <p className="text-muted-foreground">
          You need admin permissions to manage platform integrations.
        </p>
      </div>
    );
  }

  // Get platform details - hybrid approach combining backend platforms with local configs
  const getPlatformInfo = (platformKey: string) => {
    return PLATFORMS.find(p => p.value === platformKey);
  };

  // Get available platforms that have both backend support and local configuration
  const getAvailablePlatforms = () => {
    if (platformsLoading || !availablePlatforms.length) {
      return PLATFORMS; // Fallback to local platforms while loading
    }

    // Filter local platforms to only show ones supported by backend
    const backendPlatformKeys = availablePlatforms.map(p => p.value);
    return PLATFORMS.filter(platform => backendPlatformKeys.includes(platform.value));
  };

  // Handle form submission
  const onSubmitConfig = (data: PlatformConfigFormData) => {
    logInfo('Platform config form submitted', {
      component: 'ApiManagement',
      action: 'onSubmitConfig',
      metadata: { platform: data.platform, isEditing: !!editingConfig },
    });

    const platformInfo = getPlatformInfo(data.platform);
    if (!platformInfo) {
      logError('Invalid platform selected', {
        component: 'ApiManagement',
        action: 'onSubmitConfig',
        metadata: { platform: data.platform },
      });
      return;
    }

    if (editingConfig) {
      // Update existing config
      const updateData: UpdatePlatformConfigRequest = {
        name: data.name,
        settings: data.settings,
        is_enabled: data.is_enabled || false,
        auto_sync_enabled: data.auto_sync_enabled || false,
        sync_interval: data.sync_interval || 'disabled',
      };

      updateConfigMutation.mutate(
        { id: editingConfig.id, data: updateData },
        {
          onSuccess: () => {
            refetchConfigs();
            toast.success(i18n.api.toasts.configurationUpdated);
            closeDialog();
          },
          onError: (error: any) => {
            logError('Failed to update platform configuration', {
              component: 'ApiManagement',
              action: 'updateConfig',
              metadata: { configId: editingConfig.id, error: error.message },
            });
            toast.error(error.message || i18n.api.toasts.failedToUpdateConfiguration);
          },
        }
      );
    } else {
      // Create new config
      const createData: CreatePlatformConfigRequest = {
        platform: data.platform,
        name: data.name,
        settings: data.settings,
        is_enabled: data.is_enabled || false,
      };

      createConfigMutation.mutate(createData, {
        onSuccess: () => {
          refetchConfigs();
          toast.success(i18n.api.toasts.configurationCreated);
          closeDialog();
        },
        onError: (error: any) => {
          logError('Failed to create platform configuration', {
            component: 'ApiManagement',
            action: 'createConfig',
            metadata: { platform: data.platform, error: error.message },
          });
          toast.error(error.message || i18n.api.toasts.failedToCreateConfiguration);
        },
      });
    }
  };

  // Handle platform selection
  const handlePlatformSelect = (platform: string) => {
    setSelectedPlatform(platform);
    form.setValue('platform', platform); // This was missing!
    const platformInfo = getPlatformInfo(platform);
    if (platformInfo) {
      // Initialize empty settings for the platform
      const initialSettings: Record<string, string> = {};
      platformInfo.fields.forEach(field => {
        initialSettings[field.key] = '';
      });
      form.setValue('settings', initialSettings);
    }
  };

  // Handle edit
  const handleEdit = (config: ExternalPlatformConfig) => {
    setEditingConfig(config);
    setSelectedPlatform(config.platform);
    setShowCreateDialog(true);
  };

  // Handle delete
  const handleDelete = (config: ExternalPlatformConfig) => {
    const confirmMessage = i18n.api.confirmations.deleteConfiguration.replace(
      '{configName}',
      config.name
    );
    if (confirm(confirmMessage)) {
      deleteConfigMutation.mutate(config.id, {
        onSuccess: () => {
          refetchConfigs();
          toast.success(i18n.api.toasts.configurationDeleted);
        },
        onError: (error: any) => {
          logError('Failed to delete platform configuration', {
            component: 'ApiManagement',
            action: 'deleteConfig',
            metadata: { configId: config.id, error: error.message },
          });
          toast.error(error.message || i18n.api.toasts.failedToDeleteConfiguration);
        },
      });
    }
  };

  // Handle toggle enable/disable
  const handleToggleEnabled = (config: ExternalPlatformConfig) => {
    logInfo('Toggling platform enabled status', {
      component: 'ApiManagement',
      action: 'toggleEnabled',
      metadata: { configId: config.id, currentStatus: config.is_enabled },
    });

    const updateData: UpdatePlatformConfigRequest = {
      name: config.name,
      settings: config.settings,
      is_enabled: !config.is_enabled,
      auto_sync_enabled: config.auto_sync_enabled,
      sync_interval: config.sync_interval,
    };

    updateConfigMutation.mutate(
      { id: config.id, data: updateData },
      {
        onSuccess: () => {
          refetchConfigs();
          const message = !config.is_enabled
            ? i18n.api.toasts.platformEnabled
            : i18n.api.toasts.platformDisabled;
          toast.success(message);
        },
        onError: (error: any) => {
          logError('Failed to update platform status', {
            component: 'ApiManagement',
            action: 'toggleEnabled',
            metadata: { configId: config.id, error: error.message },
          });
          toast.error(error.message || i18n.api.toasts.failedToUpdatePlatformStatus);
        },
      }
    );
  };

  // Handle toggle auto sync
  const handleToggleAutoSync = (config: ExternalPlatformConfig) => {
    if (!config.is_enabled) {
      toast.error(i18n.api.toasts.platformMustBeEnabled);
      return;
    }

    logInfo('Toggling auto sync status', {
      component: 'ApiManagement',
      action: 'toggleAutoSync',
      metadata: { configId: config.id, currentStatus: config.auto_sync_enabled },
    });

    const updateData: UpdatePlatformConfigRequest = {
      name: config.name,
      settings: config.settings,
      is_enabled: config.is_enabled,
      auto_sync_enabled: !config.auto_sync_enabled,
      sync_interval: config.sync_interval,
    };

    updateConfigMutation.mutate(
      { id: config.id, data: updateData },
      {
        onSuccess: () => {
          refetchConfigs();
          const message = !config.auto_sync_enabled
            ? i18n.api.toasts.autoSyncEnabled
            : i18n.api.toasts.autoSyncDisabled;
          toast.success(message);
        },
        onError: (error: any) => {
          logError('Failed to update auto sync setting', {
            component: 'ApiManagement',
            action: 'toggleAutoSync',
            metadata: { configId: config.id, error: error.message },
          });
          toast.error(error.message || i18n.api.toasts.failedToUpdateAutoSync);
        },
      }
    );
  };

  // Handle bulk sync (sample/limited)
  const handleBulkSync = (config: ExternalPlatformConfig) => {
    if (!config.is_enabled) {
      toast.error(i18n.api.toasts.platformMustBeEnabled);
      return;
    }

    logInfo('Starting bulk sync', {
      component: 'ApiManagement',
      action: 'bulkSync',
      metadata: { configId: config.id, platform: config.platform },
    });

    bulkSyncMutation.mutate(config.id, {
      onSuccess: (data: any) => {
        refetchConfigs();
        refetchLogs();
        const message = i18n.api.toasts.syncSampleSuccess
          .replace('{platformName}', config.platform)
          .replace(
            '{count}',
            (
              data.successful_count ??
              data.total_products ??
              data.product_count ??
              data.count ??
              0
            ).toString()
          );
        toast.success(message);
      },
      onError: (error: any) => {
        logError('Failed to start bulk sync', {
          component: 'ApiManagement',
          action: 'bulkSync',
          metadata: { configId: config.id, error: error.message },
        });
        toast.error(error.message || i18n.api.toasts.failedToStartBulkSync);
      },
    });
  };

  // Handle sync all products (full sync)
  const handleSyncAll = (config: ExternalPlatformConfig) => {
    if (!config.is_enabled) {
      toast.error(i18n.api.toasts.platformMustBeEnabled);
      return;
    }

    logInfo('Starting sync all products', {
      component: 'ApiManagement',
      action: 'syncAll',
      metadata: { configId: config.id, platform: config.platform },
    });

    syncAllMutation.mutate(config.id, {
      onSuccess: (data: any) => {
        refetchConfigs();
        refetchLogs();
        const message = i18n.api.toasts.syncAllSuccess
          .replace('{platformName}', config.platform)
          .replace('{successCount}', data.successCount?.toString() || '0')
          .replace('{failedCount}', data.failedCount?.toString() || '0');
        toast.success(message);
      },
      onError: (error: any) => {
        logError('Failed to sync all products', {
          component: 'ApiManagement',
          action: 'syncAll',
          metadata: { configId: config.id, error: error.message },
        });
        toast.error(error.message || i18n.api.toasts.failedToSyncAll);
      },
    });
  };

  // Handle test connection
  const handleTestConnection = (config: ExternalPlatformConfig) => {
    logInfo('Testing connection', {
      component: 'ApiManagement',
      action: 'testConnection',
      metadata: { configId: config.id, platform: config.platform },
    });

    testConnectionMutation.mutate(config.id, {
      onSuccess: () => {
        toast.success(i18n.api.toasts.connectionTestSuccess);
      },
      onError: (error: any) => {
        logError('Connection test failed', {
          component: 'ApiManagement',
          action: 'testConnection',
          metadata: { configId: config.id, error: error.message },
        });
        toast.error(error.message || i18n.api.toasts.connectionTestFailed);
      },
    });
  };

  // Handle debug product
  const handleDebugProduct = (config: ExternalPlatformConfig) => {
    logInfo('Running debug for platform', {
      component: 'ApiManagement',
      action: 'debugProduct',
      metadata: { configId: config.id, platform: config.platform },
    });

    debugProductMutation.mutate(config.id, {
      onSuccess: (data: any) => {
        console.log('Debug data:', data);
        toast.success(i18n.api.toasts.debugSuccess);
      },
      onError: (error: any) => {
        logError('Debug failed', {
          component: 'ApiManagement',
          action: 'debugProduct',
          metadata: { configId: config.id, error: error.message },
        });
        toast.error(error.message || i18n.api.toasts.debugFailed);
      },
    });
  };

  // Handle preview mapping
  const handlePreviewMapping = (config: ExternalPlatformConfig) => {
    const token = localStorage.getItem('access_token');
    fetch(`/api/external-platform-configs/${config.id}/preview-mapping/`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        logInfo('Mapping preview generated', {
          component: 'ApiManagement',
          action: 'previewMapping',
          metadata: { configId: config.id, platform: config.platform },
        });
        toast.success(i18n.api.toasts.previewMappingSuccess);
      })
      .catch(error => {
        logError('Failed to preview mapping', {
          component: 'ApiManagement',
          action: 'previewMapping',
          metadata: { configId: config.id, error: error.message },
        });
        toast.error(i18n.api.toasts.failedToPreviewMapping);
      });
  };

  // Toggle secret visibility
  const toggleSecretVisibility = (configId: number, fieldKey: string) => {
    const key = `${configId}-${fieldKey}`;
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Close dialog and reset form
  const closeDialog = () => {
    setShowCreateDialog(false);
    setEditingConfig(null);
    setSelectedPlatform('');
  };

  // Get enabled platforms for analytics
  const enabledConfigs =
    configs?.filter((config: ExternalPlatformConfig) => config.is_enabled) || [];

  const handleDeleteConfig = (configId: number) => {
    deleteConfigMutation.mutate(configId, {
      onSuccess: () => {
        refetchConfigs();
        toast.success(i18n.api.toasts.configurationDeleted);
      },
      onError: (error: any) => {
        logError('Failed to delete platform configuration', {
          component: 'ApiManagement',
          action: 'deleteConfig',
          metadata: { configId, error: error.message },
        });
        toast.error(error.message || i18n.api.toasts.failedToDeleteConfiguration);
      },
    });
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="platforms" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="platforms">{i18n.api.tabs.platforms}</TabsTrigger>
          <TabsTrigger value="products">{i18n.api.tabs.products}</TabsTrigger>
          <TabsTrigger value="activity">{i18n.api.tabs.activity}</TabsTrigger>
          <TabsTrigger value="analytics">{i18n.api.tabs.analytics}</TabsTrigger>
        </TabsList>

        {/* Platforms Tab */}
        <TabsContent value="platforms" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{i18n.api.platforms.title}</h3>
              <p className="text-sm text-muted-foreground">{i18n.api.platforms.description}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => refetchConfigs()} variant="outline" size="sm">
                <RefreshCw className="mr-2 size-4" />
                {i18n.api.platforms.refresh}
              </Button>
              <Button onClick={() => setShowCreateDialog(true)} size="sm">
                <Plus className="mr-2 size-4" />
                {i18n.api.platforms.addPlatform}
              </Button>
            </div>
          </div>

          {configsLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="text-muted-foreground">
                {i18n.api.loadingStates.loadingConfigurations}
              </p>
            </div>
          ) : !configs || configs.length === 0 ? (
            <Card>
              <CardHeader className="py-12 text-center">
                <Store className="mx-auto mb-4 size-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">
                  {i18n.api.platforms.noPlatforms.title}
                </h3>
                <p className="mb-4 text-muted-foreground">
                  {i18n.api.platforms.noPlatforms.description}
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="mr-2 size-4" />
                  {i18n.api.platforms.noPlatforms.addFirst}
                </Button>
              </CardHeader>
            </Card>
          ) : (
            <div className="grid items-center gap-4">
              {configs.map((config: ExternalPlatformConfig) => {
                const platformInfo = getPlatformInfo(config.platform);
                return (
                  <Card key={config.id}>
                    <CardHeader className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="mt-1 flex size-12 items-center justify-center rounded-lg bg-muted">
                            <span>{platformInfo?.icon || '🔌'}</span>
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{config.name}</h3>
                              <Badge variant={config.is_enabled ? 'default' : 'secondary'}>
                                {config.is_enabled
                                  ? i18n.api.platforms.badges.enabled
                                  : i18n.api.platforms.badges.disabled}
                              </Badge>
                              {config.auto_sync_enabled && (
                                <Badge variant="outline" className="text-blue-600">
                                  <Clock className="mr-1 size-3" />
                                  {i18n.api.platforms.badges.autoSync}: {config.sync_interval}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {platformInfo?.label} • {i18n.api.platforms.created}{' '}
                              {format(new Date(config.created_at), 'MMM d, yyyy')}
                            </p>
                            {config.last_synced_at && (
                              <p className="text-xs text-muted-foreground">
                                {i18n.api.platforms.lastSync}:{' '}
                                {format(new Date(config.last_synced_at), 'MMM d, yyyy HH:mm')}
                              </p>
                            )}
                            {config.auto_sync_enabled && config.next_sync_at && (
                              <p className="text-xs text-blue-600">
                                {i18n.api.platforms.nextSync}:{' '}
                                {format(new Date(config.next_sync_at), 'MMM d, yyyy HH:mm')}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-1 flex items-start gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTestConnection(config)}
                            disabled={testConnectionMutation.isPending}
                          >
                            <TestTube className="mr-2 size-4" />
                            {i18n.api.platforms.actions.test}
                          </Button>
                          {config.is_enabled && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleBulkSync(config)}
                                disabled={bulkSyncMutation.isPending}
                              >
                                <Zap className="mr-2 size-4" />
                                {bulkSyncMutation.isPending
                                  ? i18n.api.platforms.actions.syncing
                                  : i18n.api.platforms.actions.sync}
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleSyncAll(config)}
                                disabled={syncAllMutation.isPending}
                              >
                                <RefreshCw className="mr-2 size-4" />
                                {syncAllMutation.isPending
                                  ? i18n.api.platforms.actions.syncingAll
                                  : i18n.api.platforms.actions.syncAll}
                              </Button>
                            </>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEdit(config)}>
                                <Settings className="mr-2 size-4" />
                                {i18n.api.platforms.actions.editConfiguration}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleEnabled(config)}>
                                {config.is_enabled ? (
                                  <PowerOff className="mr-2 size-4" />
                                ) : (
                                  <Power className="mr-2 size-4" />
                                )}
                                {config.is_enabled
                                  ? i18n.api.platforms.actions.disable
                                  : i18n.api.platforms.actions.enable}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleAutoSync(config)}>
                                <Clock className="mr-2 size-4" />
                                {config.auto_sync_enabled
                                  ? i18n.api.platforms.actions.disableAutoSync
                                  : i18n.api.platforms.actions.enableAutoSync}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDelete(config)}
                                className="text-red-600"
                              >
                                <Trash2 className="mr-2 size-4" />
                                {i18n.api.platforms.actions.delete}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{i18n.api.products.title}</h3>
              <p className="text-sm text-muted-foreground">{i18n.api.products.description}</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => refetchLogs()} variant="outline" size="sm">
                <RefreshCw className="mr-2 size-4" />
                {i18n.api.products.refresh}
              </Button>
            </div>
          </div>

          {!configs || configs.length === 0 ? (
            <Card>
              <CardHeader className="py-12 text-center">
                <Store className="mx-auto mb-4 size-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">
                  {i18n.api.products.noPlatforms.title}
                </h3>
                <p className="mb-4 text-muted-foreground">
                  {i18n.api.products.noPlatforms.description}
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="mr-2 size-4" />
                  {i18n.api.products.noPlatforms.addPlatform}
                </Button>
              </CardHeader>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Platform-wise product sync overview */}
              {configs
                .filter(config => config.is_enabled)
                .map((config: ExternalPlatformConfig) => {
                  const platformInfo = getPlatformInfo(config.platform);
                  const platformLogs =
                    syncLogsData?.results.filter(log => log.platform === config.platform) || [];
                  const recentLogs = platformLogs.slice(0, 5); // Show last 5 syncs per platform

                  return (
                    <Card key={config.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
                              <span>{platformInfo?.icon || '🔌'}</span>
                            </div>
                            <div>
                              <h4 className="font-semibold">{config.name}</h4>
                              <p className="text-sm text-muted-foreground">
                                {platformInfo?.label} • {platformLogs.length} total syncs
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleBulkSync(config)}
                              disabled={bulkSyncMutation.isPending}
                            >
                              <Zap className="mr-2 size-4" />
                              {bulkSyncMutation.isPending
                                ? i18n.api.products.actions.syncingSample
                                : i18n.api.products.actions.syncSample}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSyncAll(config)}
                              disabled={syncAllMutation.isPending}
                            >
                              <RefreshCw className="mr-2 size-4" />
                              {syncAllMutation.isPending
                                ? i18n.api.products.actions.syncingAll
                                : i18n.api.products.actions.syncAll}
                            </Button>
                          </div>
                        </div>
                      </CardHeader>

                      {recentLogs.length > 0 ? (
                        <CardContent className="p-0">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Product</TableHead>
                                <TableHead>Operation</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>External ID</TableHead>
                                <TableHead>Synced At</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {recentLogs.map((log: any) => (
                                <TableRow key={log.id}>
                                  <TableCell>
                                    <div>
                                      <div className="font-medium">
                                        {log.product?.name || `Product ${log.product_id}`}
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {log.product?.sku || `ID: ${log.product_id}`}
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="capitalize">
                                    {i18n.api.activity.operations[
                                      log.operation as keyof typeof i18n.api.activity.operations
                                    ] || log.operation}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      {log.success ? (
                                        <CheckCircle className="size-4 text-green-600" />
                                      ) : (
                                        <XCircle className="size-4 text-red-600" />
                                      )}
                                      <span
                                        className={log.success ? 'text-green-600' : 'text-red-600'}
                                      >
                                        {log.success ? 'Success' : 'Failed'}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {log.external_id ? (
                                      <Badge variant="outline" className="font-mono text-xs">
                                        {log.external_id}
                                      </Badge>
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {format(new Date(log.timestamp), 'MMM d, HH:mm')}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>

                          {platformLogs.length > 5 && (
                            <div className="border-t p-4 text-center">
                              <p className="text-sm text-muted-foreground">
                                {i18n.api.products.syncSummary
                                  .replace('{{showing}}', recentLogs.length.toString())
                                  .replace('{{total}}', platformLogs.length.toString())}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      ) : (
                        <CardContent className="py-8 text-center">
                          <div className="text-muted-foreground">
                            {i18n.api.products.noSyncs.replace('{{platformName}}', config.name)}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => handleBulkSync(config)}
                            disabled={bulkSyncMutation.isPending}
                          >
                            <Zap className="mr-2 size-4" />
                            {i18n.api.products.actions.startSyncing}
                          </Button>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}

              {configs.filter(config => config.is_enabled).length === 0 && (
                <Card>
                  <CardHeader className="py-12 text-center">
                    <PowerOff className="mx-auto mb-4 size-12 text-muted-foreground" />
                    <h3 className="mb-2 text-lg font-semibold">
                      {i18n.api.products.noActivePlatforms.title}
                    </h3>
                    <p className="mb-4 text-muted-foreground">
                      {i18n.api.products.noActivePlatforms.description}
                    </p>
                  </CardHeader>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{i18n.api.activity.title}</h3>
              <p className="text-sm text-muted-foreground">{i18n.api.activity.description}</p>
            </div>
            <Button onClick={() => refetchLogs()} variant="outline" size="sm">
              <RefreshCw className="mr-2 size-4" />
              {i18n.api.activity.refresh}
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{i18n.api.activity.recentSyncOperationsTitle}</CardTitle>
            </CardHeader>
            <CardContent>
              {syncLogsLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{i18n.api.activity.table.product}</TableHead>
                        <TableHead>{i18n.api.activity.table.platform}</TableHead>
                        <TableHead>{i18n.api.activity.table.operation}</TableHead>
                        <TableHead>{i18n.api.activity.table.status}</TableHead>
                        <TableHead>{i18n.api.activity.table.timestamp}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {syncLogsData?.results && syncLogsData.results.length > 0 ? (
                        syncLogsData.results.map(log => (
                          <TableRow key={log.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {log.product?.name || `Product ${log.product_id}`}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {log.product?.sku || `ID: ${log.product_id}`}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {log.platform}
                              </Badge>
                            </TableCell>
                            <TableCell className="capitalize">
                              {i18n.api.activity.operations[
                                log.operation as keyof typeof i18n.api.activity.operations
                              ] || log.operation}
                            </TableCell>
                            <TableCell>
                              <Badge variant={log.success ? 'default' : 'destructive'}>
                                {log.success
                                  ? i18n.api.activity.status.success
                                  : i18n.api.activity.status.failed}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} className="py-8 text-center">
                            <div className="text-muted-foreground">
                              {i18n.api.activity.noSyncActivityYet}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  {syncLogsData && syncLogsData.total_pages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <div className="text-sm text-muted-foreground">
                        {i18n.api.activity.showing} {(currentPage - 1) * pageSize + 1}{' '}
                        {i18n.api.activity.to}{' '}
                        {Math.min(currentPage * pageSize, syncLogsData.count)}{' '}
                        {i18n.api.activity.of} {syncLogsData.count} {i18n.api.activity.results}
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="size-4" />
                          {i18n.api.activity.previous}
                        </Button>
                        <div className="text-sm">
                          {i18n.api.activity.page} {currentPage} {i18n.api.activity.of}{' '}
                          {syncLogsData.total_pages}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setCurrentPage(p => Math.min(syncLogsData.total_pages, p + 1))
                          }
                          disabled={currentPage === syncLogsData.total_pages}
                        >
                          {i18n.api.activity.next}
                          <ChevronRight className="size-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{i18n.api.analytics.title}</h3>
              <p className="text-sm text-muted-foreground">{i18n.api.analytics.description}</p>
            </div>
            <Button onClick={() => refetchAnalytics()} variant="outline" size="sm">
              <RefreshCw className="mr-2 size-4" />
              {i18n.api.analytics.refresh}
            </Button>
          </div>

          {analyticsLoading ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 size-8 animate-spin rounded-full border-b-2 border-primary"></div>
              <p className="text-muted-foreground">
                {i18n.api.loadingStates.loadingConfigurations}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {i18n.api.analytics.connectedPlatforms}
                    </CardTitle>
                    <Store className="size-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{configs?.length || 0}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {i18n.api.analytics.activePlatforms}
                    </CardTitle>
                    <CheckCircle className="size-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{enabledConfigs.length}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {i18n.api.analytics.totalSyncs}
                    </CardTitle>
                    <BarChart3 className="size-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics?.total_syncs || '—'}</div>
                    {analytics && analytics.total_syncs > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {i18n.api.analytics.across} {analytics.platforms_count}{' '}
                        {i18n.api.analytics.platforms}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {i18n.api.analytics.successRate}
                    </CardTitle>
                    <Activity className="size-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {analytics && analytics.total_syncs > 0 ? `${analytics.success_rate}%` : '—'}
                    </div>
                    {analytics && analytics.total_syncs > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {i18n.api.analytics.successfulSyncs
                          .replace('{{successful}}', analytics.successful_syncs?.toString() || '0')
                          .replace('{{total}}', analytics.total_syncs?.toString() || '0')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>{i18n.api.analytics.platformDistributionTitle}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {getAvailablePlatforms().map(platform => {
                      const count =
                        configs?.filter(config => config.platform === platform.value).length || 0;
                      const platformSyncs =
                        syncLogsData?.results.filter(log => log.platform === platform.value)
                          .length || 0;
                      return (
                        <div key={platform.value} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span>{platform.icon}</span>
                            <span className="font-medium">{platform.label}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">
                              {count} {i18n.api.analytics.connected}
                            </Badge>
                            {platformSyncs > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                {platformSyncs} {i18n.api.analytics.syncs}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Platform Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={closeDialog}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[900px]">
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {editingConfig
                ? i18n.api.platforms.actions.editConfiguration
                : i18n.api.platforms.actions.addPlatform}
            </DialogTitle>
            <DialogDescription>
              {editingConfig
                ? i18n.api.platforms.actions.editDescription
                : i18n.api.platforms.actions.addDescription}
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto">
            <ConnectorForm
              editingConfig={editingConfig}
              selectedPlatform={selectedPlatform}
              setSelectedPlatform={setSelectedPlatform}
              onSubmit={onSubmitConfig}
              onCancel={closeDialog}
              isLoading={createConfigMutation.isPending || updateConfigMutation.isPending}
              attributes={attributes}
              priceTypes={priceTypes}
              showSecrets={showSecrets}
              toggleSecretVisibility={toggleSecretVisibility}
              i18n={i18n}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
